import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from 'config/axiosInstance';
import ClientInfo from './ClientInfo';
import TaskAssignment from './TaskAssignment';
import DriverAssignment from './DriverAssignment';
import { useNavigate, useParams } from 'react-router-dom';
import { Task } from '../../../reusable/Client/dialog/AddTaskDialog';
import {
  secondsToHoursAndMinutes,
  timeStringToSeconds,
} from '../Utility/timeHelpers';
import DiscardChangesDialog from '../../../reusable/Client/dialog/DiscardChangesDialog';
import SaveChangesDialog from '../../../reusable/Client/dialog/SaveChangesDialog';
import { useTranslation } from 'react-i18next';

interface Client {
  name: string;
  description: string;
  tasks: number[];
  assign_drivers: number[];
}

const EditClient: React.FC = () => {
  const { t } = useTranslation('client');
  const [client, setClient] = useState<Client>({
    name: '',
    description: '',
    tasks: [],
    assign_drivers: [],
  });

  const [totalDurationWithTasks, setTotalDurationWithTasks] = useState<{
    hours: number;
    minutes: number;
  } | null>(null);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [selectedDrivers, setSelectedDrivers] = useState<number[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  // const [isRemoveTaskPopupOpen, setIsRemoveTaskPopupOpen] = useState(false);
  // const [isSaveChangesPopupOpen, setIsSaveChangesPopupOpen] = useState(false);
  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] =
    useState(false);
  const [isSaveChangesDialogOpen, setIsSaveChangesDialogOpen] = useState(false);
  const [selectedTaskIdForRemoval, setSelectedTaskIdForRemoval] = useState<
    number | null
  >(null);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [showErrorTooltip, setShowErrorTooltip] = useState(false);

  // Fetch client and related data
  const fetchData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/v1/client_deliveries/${id}/`);
      const clientData = response.data;
      setClient(clientData);
      setSelectedDrivers(clientData.assign_drivers || []);

      // Fetch all tasks and drivers to populate the lists
      const [tasksResponse, driversResponse, totalDurationResponsewithTask] =
        await Promise.all([
          axiosInstance.get(`/v1/tasks/?page_size=0`),
          axiosInstance.get(`/v1/drivers/?page_size=0`),
          axiosInstance.get(
            `/v1/client_deliveries/summarize_spent_hour_with_tasks/?id__in=${id}&start_time=2024-01-01T00%3A00%3A00.000Z&stop_time=2024-12-31T00%3A00%3A00.000Z`
          ),
        ]);

      const fetchedTasks = tasksResponse.data;
      const fetchedDrivers = driversResponse.data;

      // Set tasks and drivers in state
      setTasks(fetchedTasks);
      setDrivers(fetchedDrivers);

      // Map over clientData.tasks to find corresponding task details
      const selectedTasksWithDetails = clientData.tasks.map(
        (taskId: number) => {
          const taskDetails = fetchedTasks.find(
            (task: Task) => task.id === taskId
          );
          return {
            id: taskId,
            name: taskDetails ? taskDetails.name : '',
            icon: taskDetails ? taskDetails.icon : 'default',
            isDisabled: true,
          };
        }
      );

      // Update selectedTasks with the correct task details
      setSelectedTasks(selectedTasksWithDetails);

      // Calculate total duration with tasks
      const hourSpentArray =
        totalDurationResponsewithTask.data[0]?.summarize || [];
      const totalSeconds = hourSpentArray.reduce(
        (total: number, entry: any) => {
          return total + timeStringToSeconds(entry.hour_spent);
        },
        0
      );

      setTotalDurationWithTasks(
        totalSeconds > 0 ? secondsToHoursAndMinutes(totalSeconds) : null
      );
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle form submission to update the client
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (selectedTasks.length === 0) {
      setShowErrorTooltip(true);
      return;
    }
    try {
      const updatedClient = {
        name: client.name,
        description: client.description,
        assign_drivers: selectedDrivers,
        tasks: selectedTasks.map((task) => task.id),
      };

      await axiosInstance.patch(`/v1/client_deliveries/${id}/`, updatedClient);
      setIsSaveChangesDialogOpen(true);
    } catch (error) {
      console.error('Failed to update client', error);
    }
  };

  return (
    <section className="bg-white rounded-md m-4">
      <form onSubmit={handleSubmit}>
        <ClientInfo
          clientName={client.name}
          setClientName={(name: string) =>
            setClient((prevClient) => ({
              ...prevClient,
              name,
            }))
          }
          clientDescription={client.description}
          setClientDescription={(description: string) =>
            setClient((prevClient) => ({
              ...prevClient,
              description,
            }))
          }
          totalDurationWithTasks={totalDurationWithTasks}
          activeTasks={client.tasks.length}
          driversAssigned={client.assign_drivers.length}
        />
        <hr />
        <TaskAssignment
          selectedTasks={selectedTasks}
          setSelectedTasks={setSelectedTasks}
          showErrorTooltip={showErrorTooltip}
          setShowErrorTooltip={() => setShowErrorTooltip(false)}
          isRemoveTask={true}
        />
        <hr />

        <DriverAssignment
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          drivers={drivers}
          selectedDrivers={selectedDrivers}
          handleDriverSelect={(driver) =>
            setSelectedDrivers((prev) => [...prev, driver.id])
          }
          handleDriverRemove={(driverId) =>
            setSelectedDrivers((prev) => prev.filter((id) => id !== driverId))
          }
        />
        <div className="flex justify-end mt-4 gap-4 border-t p-4">
          <button
            type="button"
            className="bg-gray-200 font-semi-bold text-subtitle-2 text-primary/300 h-12 py-2 px-20 rounded-md"
            onClick={() => setIsDiscardChangesDialogOpen(true)}
          >
            {t('action.discard')}
          </button>
          <button
            type="submit"
            className="bg-primary/300 font-medium text-subtitle-2 text-white h-12 py-2 px-12 rounded-md"
          >
            {t('action.save_changes')}
          </button>
        </div>
      </form>

      <DiscardChangesDialog
        isOpen={isDiscardChangesDialogOpen}
        onCancel={() => setIsDiscardChangesDialogOpen(false)}
        onDiscard={() => {
          setIsDiscardChangesDialogOpen(false);
          navigate(-1);
        }}
      />

      <SaveChangesDialog
        isOpen={isSaveChangesDialogOpen}
        onClose={() => setIsSaveChangesDialogOpen(false)}
      />
    </section>
  );
};

export default EditClient;
